"use client"

import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { guessAdminRoute } from "../../../../utility/Helper1"
import { authActions } from "../../../../redux/actions"
import { allRemoteSettingsSelect, userSelect } from "../../../../redux/selectors"

// MUI Components
import {
  Avatar,
  Divider,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Fab,
  Tooltip,
  Badge,
  CardContent,
  Chip,
  Paper,
} from "@mui/material"

// Lucide Icons
import {
  LayoutDashboard,
  Wallet,
  BarChart3,
  Settings,
  Gift,
  LogOut,
  ChevronDown,
  ChevronUp,
  MenuIcon,
  X,
  MessageCircle,
  Send,
  Users,
  Bell,
  BellRing,
  ExternalLink,
  Clock,
  Calendar,
  AlertCircle,
  Info,
  CheckCircle,
} from "lucide-react"

// Cookie utility functions
const getCookie = (name) => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop().split(";").shift()
  return null
}

const setCookie = (name, value, days = 7) => {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/`
}

const deleteCookie = (name) => {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
}

export default function WrapUpP2p({ children }) {
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [isMobile, setIsMobile] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [profileAnchorEl, setProfileAnchorEl] = useState(null)

  // New state for added features
  const [newsDialogOpen, setNewsDialogOpen] = useState(false)
  const [hasUnreadNews, setHasUnreadNews] = useState(true)
  const [supportMenuOpen, setSupportMenuOpen] = useState(false)
  const [selectedNewsItem, setSelectedNewsItem] = useState(null)

  const userData = useSelector(userSelect)
  const site_settings = useSelector(allRemoteSettingsSelect)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  // Get news from site_settings
  const newsItems = site_settings?.news || []

  // Check if mobile on mount and window resize
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
      setSidebarOpen(!mobile)
    }

    checkMobile()
    window.addEventListener("resize", checkMobile)

    return () => {
      window.removeEventListener("resize", checkMobile)
    }
  }, [])

  // Auto-popup news modal when cookie is not set or expired
  useEffect(() => {
    // Only show if there are news items
    if (newsItems.length > 0) {
      const newsReadCookie = getCookie("news_already_read")

      if (!newsReadCookie) {
        // Small delay to ensure component is fully mounted
        const timer = setTimeout(() => {
          setNewsDialogOpen(true)
          setHasUnreadNews(true)
        }, 1000)

        return () => clearTimeout(timer)
      } else {
        // Check if cookie has expired (optional - you can customize this logic)
        try {
          const cookieDate = new Date(newsReadCookie)
          const now = new Date()
          const daysSinceCookieSet = Math.floor((now - cookieDate) / (1000 * 60 * 60 * 24))

          // Auto-show again after 7 days (customize as needed)
          if (daysSinceCookieSet >= 7) {
            deleteCookie("news_already_read")
            const timer = setTimeout(() => {
              setNewsDialogOpen(true)
              setHasUnreadNews(true)
            }, 1000)

            return () => clearTimeout(timer)
          }
        } catch (error) {
          // If cookie date is invalid, delete it and show modal
          deleteCookie("news_already_read")
          const timer = setTimeout(() => {
            setNewsDialogOpen(true)
            setHasUnreadNews(true)
          }, 1000)

          return () => clearTimeout(timer)
        }
      }
    }
  }, [newsItems.length])

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Recently"

    try {
      const date = new Date(timestamp)
      const now = new Date()
      const diffInHours = Math.floor((now - date) / (1000 * 60 * 60))

      if (diffInHours < 1) return "Just now"
      if (diffInHours < 24) return `${diffInHours} hours ago`
      if (diffInHours < 48) return "Yesterday"

      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
      })
    } catch (error) {
      return "Recently"
    }
  }

  // Get priority icon based on keywords in subject
  const getPriorityInfo = (subject) => {
    const subjectLower = subject?.toLowerCase() || ""

    if (subjectLower.includes("urgent") || subjectLower.includes("critical") || subjectLower.includes("important")) {
      return { icon: <AlertCircle size={16} />, color: "error", label: "Important" }
    }
    if (subjectLower.includes("update") || subjectLower.includes("new") || subjectLower.includes("feature")) {
      return { icon: <Info size={16} />, color: "info", label: "Update" }
    }
    if (subjectLower.includes("maintenance") || subjectLower.includes("scheduled")) {
      return { icon: <Clock size={16} />, color: "warning", label: "Maintenance" }
    }
    return { icon: <CheckCircle size={16} />, color: "success", label: "General" }
  }

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen)
  }

  const handleLogout = () => {
    dispatch(authActions.onLogout())
  }

  const handleProfileClick = (event) => {
    setProfileAnchorEl(event.currentTarget)
  }

  const handleProfileClose = () => {
    setProfileAnchorEl(null)
  }

  const toggleSettings = () => {
    setSettingsOpen(!settingsOpen)
  }

  const handleNavigation = (path) => {
    navigate(path)
    if (isMobile) {
      setSidebarOpen(false)
    }
  }

  const handleNewsOpen = () => {
    setNewsDialogOpen(true)
    setHasUnreadNews(false)
  }

  const handleNewsClose = () => {
    setNewsDialogOpen(false)
    setSelectedNewsItem(null)

    // Set cookie to indicate user has seen the news
    if (newsItems.length > 0) {
      setCookie("news_already_read", new Date().toISOString(), 7) // Expires in 7 days
    }
  }

  const handleNewsItemClick = (newsItem) => {
    setSelectedNewsItem(newsItem)
  }

  const handleBackToNewsList = () => {
    setSelectedNewsItem(null)
  }

  const handleWhatsAppSupport = () => {
    window.open(site_settings?.site_settings?.whatsappSupport, "_blank")
  }

  const handleTelegramSupport = () => {
    window.open(site_settings?.site_settings?.telegramSupport, "_blank")
  }

  const handleWhatsAppGroup = () => {
    window.open(site_settings?.site_settings?.whatsapp, "_blank")
  }

  const handleTelegramCommunity = () => {
    window.open(site_settings?.site_settings?.telegram, "_blank")
  }

  const menuItems = [
    { name: "Dashboard", icon: <LayoutDashboard size={20} />, path: "/" },
    { name: "My Portfolio", icon: <Wallet size={20} />, path: "/portfolio" },
    { name: "Earning", icon: <BarChart3 size={20} />, path: "/withdraw/earning" },
  ]

  const bottomMenuItems = [
    { name: "Affiliate", icon: <Gift size={20} />, path: "/referral/system" },
    { name: "Log Out", icon: <LogOut size={20} />, onClick: handleLogout },
  ]

  const settingsItems = [
    { name: "Payment Profile", path: "/settings-payment-method" },
    { name: "My Account", path: "/settings-profile" },
    { name: "Security", path: "/settings-password" },
  ]

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Mobile overlay */}
      {isMobile && sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed md:relative z-30 h-full bg-gradient-to-b from-slate-800 to-slate-900 
          transition-all duration-300 ease-in-out
          ${isMobile ? (sidebarOpen ? "w-64 left-0" : "w-0 -left-full") : sidebarOpen ? "w-64" : "w-20"}
        `}
      >
        {/* Logo and toggle */}
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center space-x-3 overflow-hidden">
            <Avatar
              src={`${guessAdminRoute()}/${site_settings?.site_settings?.logo}`}
              className="h-8 w-8 flex-shrink-0"
            />
            {sidebarOpen && (
              <span className="font-medium text-indigo-400">{site_settings?.site_settings?.sitename}</span>
            )}
          </div>
          <button onClick={toggleSidebar} className="text-gray-400 hover:text-white">
            {sidebarOpen ? <X size={20} /> : <MenuIcon size={20} />}
          </button>
        </div>

        <Divider className="bg-slate-700 opacity-30" />

        {/* User profile */}
        <div className="flex flex-col items-center py-6">
          <Avatar
            src={`${guessAdminRoute()}/${userData?.image}`}
            className={`border-2 border-indigo-500 ${sidebarOpen ? "h-20 w-20" : "h-10 w-10"}`}
          />
          {sidebarOpen && (
            <div className="mt-3 text-center">
              <div className="font-medium text-white">{userData?.username}</div>
              <div className="text-sm text-gray-400">{userData?.email}</div>
            </div>
          )}
        </div>

        <Divider className="bg-slate-700 opacity-30" />

        {/* Navigation */}
        <div className="mt-6 px-3">
          <ul className="space-y-2">
            {menuItems.map((item, index) => (
              <li key={index}>
                <button
                  onClick={() => handleNavigation(item.path)}
                  className="flex items-center w-full px-3 py-2 text-gray-300 hover:bg-slate-700 rounded-md transition-all duration-200"
                >
                  <span className="flex-shrink-0">{item.icon}</span>
                  {sidebarOpen && <span className="ml-4">{item.name}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Bottom section */}
        <div className="absolute bottom-0 left-0 right-0 pb-4">
          <Divider className="bg-slate-700 opacity-30 mb-4" />

          {/* Settings */}
          <div className="px-3">
            <button
              onClick={toggleSettings}
              className="flex items-center justify-between w-full px-3 py-2 text-gray-300 hover:bg-slate-700 rounded-md transition-all duration-200"
            >
              <div className="flex items-center">
                <Settings size={20} />
                {sidebarOpen && <span className="ml-4">Settings</span>}
              </div>
              {sidebarOpen && (settingsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />)}
            </button>

            {/* Settings dropdown */}
            {settingsOpen && sidebarOpen && (
              <div className="mt-1 ml-8 space-y-1">
                {settingsItems.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => handleNavigation(item.path)}
                    className="w-full px-4 py-2 text-left text-gray-300 hover:bg-slate-700 rounded-md transition-all duration-200"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}

            {/* Bottom menu items */}
            <ul className="mt-2 space-y-2">
              {bottomMenuItems.map((item, index) => (
                <li key={index}>
                  <button
                    onClick={item.onClick || (() => handleNavigation(item.path))}
                    className="flex items-center w-full px-3 py-2 text-gray-300 hover:bg-slate-700 rounded-md transition-all duration-200"
                  >
                    <span className="flex-shrink-0">{item.icon}</span>
                    {sidebarOpen && <span className="ml-4">{item.name}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <button onClick={toggleSidebar} className="md:hidden text-gray-600 hover:text-gray-900 mr-4">
                <MenuIcon size={24} />
              </button>
              <div className="text-gray-800 font-medium">{userData?.username},</div>
            </div>

            {/* Header Actions */}
            <div className="flex items-center space-x-4">
              {/* News Bell */}
              <Tooltip title="News & Updates">
                <IconButton onClick={handleNewsOpen} className="text-gray-600 hover:text-blue-600">
                  <Badge badgeContent={newsItems.length > 0 ? newsItems.length : 0} color="error">
                    {newsItems.length > 0 ? <BellRing size={20} /> : <Bell size={20} />}
                  </Badge>
                </IconButton>
              </Tooltip>

              {/* Profile menu */}
              <div className="flex items-center space-x-3 cursor-pointer" onClick={handleProfileClick}>
                <Avatar src={`${guessAdminRoute()}/${userData?.image}`} className="h-9 w-9" />
                <span className="text-gray-700 text-sm hidden sm:block">{userData?.username}</span>
              </div>
            </div>

            <Menu
              anchorEl={profileAnchorEl}
              open={Boolean(profileAnchorEl)}
              onClose={handleProfileClose}
              PaperProps={{
                className: "mt-2 shadow-lg",
              }}
            >
              <MenuItem
                onClick={() => {
                  handleNavigation("/settings-profile")
                  handleProfileClose()
                }}
              >
                Profile
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleNavigation("/settings-payment-method")
                  handleProfileClose()
                }}
              >
                Billing
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 overflow-auto p-4 md:p-6 bg-gradient-to-br from-gray-50 to-gray-100">{children}</div>
      </div>

      {/* Floating Action Buttons - Support & Community */}
      <div className="fixed bottom-6 right-6 flex flex-col space-y-3 z-40">
        {/* Community Groups */}
        <div className="flex space-x-3">
          <Tooltip title="WhatsApp Community">
            <Fab
              size="medium"
              onClick={handleWhatsAppGroup}
              className="bg-green-500 hover:bg-green-600 text-white shadow-lg"
            >
              <Users size={20} />
            </Fab>
          </Tooltip>
          <Tooltip title="Telegram Community">
            <Fab
              size="medium"
              onClick={handleTelegramCommunity}
              className="bg-blue-500 hover:bg-blue-600 text-white shadow-lg"
            >
              <Send size={20} />
            </Fab>
          </Tooltip>
        </div>

        {/* Live Support */}
        <div className="relative" style={{ zIndex: 9999 }}>
          <Tooltip title="Live Support">
            <Fab
              onClick={() => setSupportMenuOpen(!supportMenuOpen)}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white shadow-lg"
            >
              <MessageCircle size={24} />
            </Fab>
          </Tooltip>

          {/* Support Menu */}
          {supportMenuOpen && (
            <div className="absolute bottom-16 right-0 bg-white rounded-lg shadow-xl border p-2 min-w-48">
              <div className="text-sm font-medium text-gray-700 px-3 py-2 border-b">Live Support</div>
              <button
                onClick={handleWhatsAppSupport}
                className="flex items-center w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors"
              >
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-3">
                  <MessageCircle size={16} className="text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-800">WhatsApp</div>
                  <div className="text-xs text-gray-500">Chat with us</div>
                </div>
              </button>
              <button
                onClick={handleTelegramSupport}
                className="flex items-center w-full px-3 py-2 text-left hover:bg-gray-50 rounded-md transition-colors"
              >
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-3">
                  <Send size={16} className="text-white" />
                </div>
                <div>
                  <div className="font-medium text-gray-800">Telegram</div>
                  <div className="text-xs text-gray-500">Get instant help</div>
                </div>
              </button>
              <div className="px-3 py-2 text-xs text-gray-500 border-t">
                <div className="flex items-center">
                  <div className="w-2 h-2 bg-green-400 rounded-full mr-2"></div>
                  Online • Response within 5 mins
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Click outside to close support menu */}
      {supportMenuOpen && <div className="fixed inset-0 z-30" onClick={() => setSupportMenuOpen(false)} />}

      {/* Enhanced News Dialog */}
      <Dialog
        open={newsDialogOpen}
        onClose={handleNewsClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: "rounded-xl shadow-2xl",
          style: { minHeight: "500px" },
        }}
      >
        <DialogTitle className="flex items-center justify-between border-b bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BellRing className="text-blue-600" size={24} />
            </div>
            <div>
              <span className="font-bold text-gray-800 text-lg">
                {selectedNewsItem ? "News Details" : "Latest News & Updates"}
              </span>
              {!selectedNewsItem && (
                <div className="text-sm text-gray-600">
                  {newsItems.length} {newsItems.length === 1 ? "update" : "updates"} available
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {selectedNewsItem && (
              <IconButton onClick={handleBackToNewsList} size="small" className="text-gray-600">
                <ChevronDown size={20} style={{ transform: "rotate(90deg)" }} />
              </IconButton>
            )}
            <IconButton onClick={handleNewsClose} size="small" className="text-gray-600">
              <X size={20} />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="p-0">
          {selectedNewsItem ? (
            // Detailed News View
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-xl font-bold text-gray-800">{selectedNewsItem.subject}</h2>
                  <Chip
                    icon={getPriorityInfo(selectedNewsItem.subject).icon}
                    label={getPriorityInfo(selectedNewsItem.subject).label}
                    color={getPriorityInfo(selectedNewsItem.subject).color}
                    variant="outlined"
                    size="small"
                  />
                </div>
                <div className="flex items-center text-gray-500 text-sm mb-4">
                  <Calendar size={14} className="mr-2" />
                  {formatTimestamp(selectedNewsItem.timestamp)}
                </div>
              </div>

              <Divider className="mb-4" />

              <div
                className="prose prose-sm max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedNewsItem.message }}
                style={{
                  lineHeight: "1.6",
                  fontSize: "14px",
                }}
              />
            </div>
          ) : (
            // News List View
            <div className="max-h-96 overflow-y-auto">
              {newsItems.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                  <Bell size={48} className="mb-4 opacity-50" />
                  <h3 className="text-lg font-medium mb-2">No news available</h3>
                  <p className="text-sm text-center">{"We'll notify you when there are new updates."}</p>
                </div>
              ) : (
                <div className="space-y-3 p-4">
                  {newsItems.map((item, index) => {
                    const priorityInfo = getPriorityInfo(item.subject)
                    return (
                      <Paper
                        key={index}
                        elevation={1}
                        className="border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-200 cursor-pointer"
                        onClick={() => handleNewsItemClick(item)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center space-x-3 flex-1">
                              <div
                                className={`p-2 rounded-lg bg-${priorityInfo.color === "error" ? "red" : priorityInfo.color === "warning" ? "yellow" : priorityInfo.color === "info" ? "blue" : "green"}-100`}
                              >
                                {priorityInfo.icon}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-800 text-sm mb-1 line-clamp-1">
                                  {item.subject}
                                </h4>
                                <div
                                  className="text-gray-600 text-xs line-clamp-2"
                                  dangerouslySetInnerHTML={{
                                    __html: item.message?.replace(/<[^>]*>/g, "").substring(0, 100) + "...",
                                  }}
                                />
                              </div>
                            </div>
                            <Chip
                              label={priorityInfo.label}
                              color={priorityInfo.color}
                              variant="outlined"
                              size="small"
                              className="ml-2 flex-shrink-0"
                            />
                          </div>

                          <div className="flex items-center justify-between">
                            <div className="flex items-center text-gray-500 text-xs">
                              <Clock size={12} className="mr-1" />
                              {formatTimestamp(item.timestamp)}
                            </div>
                            <div className="flex items-center text-blue-600 text-xs font-medium">
                              Read more
                              <ExternalLink size={12} className="ml-1" />
                            </div>
                          </div>
                        </CardContent>
                      </Paper>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="border-t bg-gray-50 p-4 rounded-b-xl">
          <div className="flex items-center justify-between w-full">
            {/* <Button
              onClick={() => {
                // Set cookie for longer period (30 days) for "don't show again"
                setCookie("news_already_read", new Date().toISOString(), 30)
                handleNewsClose()
              }}
              variant="text"
              className="text-gray-600 hover:text-gray-800"
              size="small"
            >
              Don't show again
            </Button> */}
            <span></span>
            <Button
              onClick={handleNewsClose}
              variant="text"
              className="text-gray-600 hover:text-gray-800"
            >
              Close
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </div>
  )
}
